"use client";

import type { FormEvent } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useBag } from "@/hooks/useBag";
import { composeCustomOrderSpec } from "@/lib/customOrderSpec";
import type { BagPage } from "@/sanity/lib/types";

const USER_STORAGE_KEY = "vault88-user";
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function CartClient({
  content,
  heroImageUrl,
  ownerActive,
}: {
  content: BagPage;
  heroImageUrl: string | null;
  ownerActive: boolean;
}) {
  const { items, removeItem, refresh, clearBag } = useBag();
  const router = useRouter();
  const hasItems = items.length > 0;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [body, setBody] = useState(content.defaultEmailBody ?? "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const toastTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [portalReady, setPortalReady] = useState(false);
  const [brokenImageIds, setBrokenImageIds] = useState<Set<string>>(new Set());
  const emailValid = useMemo(() => emailRegex.test(email.trim()), [email]);

  const itemIdsKey = items.map((item) => item.id).sort().join(",");
  useEffect(() => {
    if (typeof window === "undefined") return;
    const ids = itemIdsKey ? itemIdsKey.split(",") : [];
    if (ids.length === 0) return;

    let cancelled = false;
    (async () => {
      try {
        const response = await fetch("/api/items/refresh-images", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ids })
        });
        if (!response.ok) return;
        const data = (await response.json()) as { items?: Array<{ id: string; pictureUrl: string }> };
        if (cancelled || !data.items?.length) return;

        const stored = window.localStorage.getItem("vault88-cart");
        if (!stored) return;
        const parsed = JSON.parse(stored) as Array<{ id: string; pictureUrl: string }>;
        const freshById = new Map(data.items.map((i) => [i.id, i.pictureUrl]));
        let changed = false;
        const next = parsed.map((entry) => {
          const fresh = freshById.get(entry.id);
          if (fresh && fresh !== entry.pictureUrl) {
            changed = true;
            return { ...entry, pictureUrl: fresh };
          }
          return entry;
        });
        if (!changed) return;
        window.localStorage.setItem("vault88-cart", JSON.stringify(next));
        window.dispatchEvent(new StorageEvent("storage", { key: "vault88-cart", newValue: JSON.stringify(next) }));
        setBrokenImageIds(new Set());
        refresh();
      } catch (error) {
        console.error("[Bag] Failed to refresh image URLs", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [itemIdsKey, refresh]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const stored = window.localStorage.getItem(USER_STORAGE_KEY);
      if (!stored) return;
      const parsed = JSON.parse(stored) as { email?: string; firstName?: string; lastName?: string };
      if (parsed.email) {
        setEmail((prev) => prev || parsed.email || "");
      }
      if (parsed.firstName) {
        setFirstName((prev) => prev || parsed.firstName || "");
      }
      if (parsed.lastName) {
        setLastName((prev) => prev || parsed.lastName || "");
      }
    } catch (error) {
      console.error("[Bag] Failed to read cached user", error);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setPortalReady(true);
  }, []);

  const handleSendEmail = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!emailValid || items.length === 0) return;

    setIsSubmitting(true);
    setSubmitMessage(null);
    try {
      const response = await fetch("/api/inquiries", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          email: email.trim(),
          firstName: firstName.trim() || undefined,
          lastName: lastName.trim() || undefined,
          emailBody: body.trim(),
          pieceNames: items.map((item) => item.itemName).filter(Boolean),
          pieceDetails: items
            .filter((item) => item.itemName)
            .map((item) => ({
              name: item.itemName,
              size: item.size,
              requestedSize: item.requestedSize
            }))
        })
      });

      if (!response.ok) {
        const data = (await response.json().catch(() => null)) as { error?: string } | null;
        throw new Error(data?.error || "Failed to submit inquiry");
      }
      setSubmitMessage(null);
      setIsModalOpen(false);
      setToastMessage(content.successMessage ?? "Email sent.");
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
      toastTimeoutRef.current = setTimeout(() => {
        setToastMessage(null);
      }, 3000);
    } catch (error) {
      console.error("[Bag] Failed to submit inquiry", error);
      setSubmitMessage(error instanceof Error ? error.message : "Sorry, something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Owner-only: go to the order-review page, where the owner edits prices /
  // discounts before the Stripe session is created.
  const goToReview = () => {
    if (items.length === 0) return;
    router.push("/checkout/review");
  };

  return (
    <div className="-mt-[48px] sm:-mt-[84px]">
      {heroImageUrl ? (
        <section className="relative h-[55vh] min-h-[360px] w-full overflow-hidden">
          <Image
            src={heroImageUrl}
            alt={content.heroImage?.alt ?? content.heading ?? "Your Bag"}
            fill
            priority
            className="object-cover"
            sizes="100vw"
          />
          <div className="absolute inset-0 bg-black/30" />
          <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
            <h1 className="text-4xl font-semibold text-white font-[var(--font-display)] sm:text-5xl drop-shadow-lg">
              {content.heading}
            </h1>
          </div>
        </section>
      ) : null}

      <div className="px-4 py-24">
        {portalReady
          ? createPortal(
              <AnimatePresence>
                {toastMessage ? (
                  <motion.div
                    key="cart-toast"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 px-4"
                  >
                    <motion.div
                      initial={{ scale: 0.92, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.92, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="w-full max-w-xl rounded-3xl bg-white px-10 py-10 text-center"
                    >
                      <p className="text-3xl font-semibold text-slate-900">{toastMessage}</p>
                    </motion.div>
                  </motion.div>
                ) : null}
              </AnimatePresence>,
              document.body
            )
          : null}
        <div className="mx-auto max-w-4xl space-y-6 text-center">
          {!heroImageUrl ? (
            <h1 className="text-4xl font-semibold text-slate-900 font-[var(--font-display)]">{content.heading}</h1>
          ) : null}
          {hasItems ? (
            <>
              <p className="text-slate-600">{content.subtitle}</p>
              <div className="border border-slate-200 bg-white text-left">
                {items.map((item, index) => (
                  <article
                    key={item.id}
                    className={`relative flex flex-col gap-4 p-4 sm:flex-row ${index < items.length - 1 ? "border-b border-slate-200" : ""}`}
                  >
                    <button
                      type="button"
                      onClick={() => removeItem(item.id)}
                      className="absolute right-3 top-3 hidden h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-sm text-slate-500 shadow-sm transition hover:border-slate-400 hover:text-slate-900 sm:inline-flex"
                      aria-label="Remove item"
                    >
                      ✕
                    </button>
                    {item.isCustomOrder ? (
                      <div className="relative block h-48 w-full flex-shrink-0 overflow-hidden bg-white sm:h-40 sm:w-[200px]">
                        {item.pictureUrl && !brokenImageIds.has(item.id) ? (
                          // eslint-disable-next-line @next/next/no-img-element
                          <img
                            src={item.pictureUrl}
                            alt={item.itemName || "Custom order"}
                            className="h-full w-full object-contain"
                            onError={() =>
                              setBrokenImageIds((prev) => {
                                if (prev.has(item.id)) return prev;
                                const next = new Set(prev);
                                next.add(item.id);
                                return next;
                              })
                            }
                          />
                        ) : (
                          <div className="flex h-full items-center justify-center text-slate-400">
                            Custom request
                          </div>
                        )}
                      </div>
                    ) : (
                      <Link href={`/pieces/${item.id}`} className="relative block h-48 w-full flex-shrink-0 overflow-hidden bg-white sm:h-40 sm:w-[200px]">
                        {item.pictureUrl && !brokenImageIds.has(item.id) ? (
                          <Image
                            src={item.pictureUrl}
                            alt={item.itemName || "Bag item"}
                            fill
                            className="object-contain"
                            sizes="(max-width: 768px) 90vw, 200px"
                            onError={() =>
                              setBrokenImageIds((prev) => {
                                if (prev.has(item.id)) return prev;
                                const next = new Set(prev);
                                next.add(item.id);
                                return next;
                              })
                            }
                          />
                        ) : (
                          <div className="flex h-full items-center justify-center text-slate-400">Image coming soon</div>
                        )}
                      </Link>
                    )}
                    <div className="flex flex-1 flex-col justify-between gap-3 sm:pr-8">
                      <div>
                        {item.isCustomOrder ? (
                          <p className="text-lg font-semibold text-slate-900">
                            {item.itemName || "Custom Order Request"}
                          </p>
                        ) : (
                          <Link href={`/pieces/${item.id}`} className="text-lg font-semibold text-slate-900 hover:underline hover:underline-offset-2">
                            {item.itemName || "Untitled"}
                          </Link>
                        )}
                        {item.isCustomOrder ? (
                          <>
                            {(() => {
                              const spec = composeCustomOrderSpec(item.customOrder ?? {});
                              return spec ? (
                                <p className="text-sm text-slate-600">{spec}</p>
                              ) : null;
                            })()}
                            {item.customOrder?.description ? (
                              <p className="text-sm text-slate-500">{item.customOrder.description}</p>
                            ) : null}
                          </>
                        ) : (
                          <>
                            <p className="text-sm text-slate-600">{item.metal || "Mixed metal"}</p>
                            <p className="text-sm text-slate-600">{item.stone || "Varied stone"}</p>
                            {item.size ? (
                              <p className="text-sm text-slate-600">Size {item.size}</p>
                            ) : null}
                            {item.requestedSize ? (
                              <p className="text-sm font-medium text-amber-700">
                                Requested adjustment: size {item.requestedSize}
                              </p>
                            ) : null}
                          </>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-slate-900">
                          {item.tagPrice ? `$${item.tagPrice.toLocaleString()}` : "Price on request"}
                        </p>
                        <button
                          type="button"
                          onClick={() => removeItem(item.id)}
                          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.2em] text-slate-400 transition hover:text-slate-900 sm:hidden"
                          aria-label="Remove item"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
              <div className="flex flex-col items-center gap-3">
                <div className="flex flex-wrap items-center justify-center gap-3">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(true)}
                    className="inline-flex items-center justify-center rounded-sm bg-royal px-6 py-3 text-sm font-semibold uppercase tracking-[0.3em] text-white transition hover:-translate-y-0.5"
                  >
                    {content.contactButtonText}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm("Remove all items from your bag?")) {
                        clearBag();
                      }
                    }}
                    className="inline-flex items-center justify-center rounded-sm border border-slate-300 px-6 py-3 text-sm font-semibold uppercase tracking-[0.3em] text-slate-600 transition hover:border-slate-400 hover:text-slate-900"
                  >
                    Empty bag
                  </button>
                  {ownerActive ? (
                    <button
                      type="button"
                      onClick={goToReview}
                      className="inline-flex items-center justify-center rounded-sm bg-[#c4a668] px-6 py-3 text-sm font-semibold uppercase tracking-[0.3em] text-white transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Checkout
                    </button>
                  ) : null}
                </div>
              </div>
            </>
          ) : (
            <p className="text-slate-600">{content.emptyMessage}</p>
          )}
        </div>
        {isModalOpen ? (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 py-8"
            onClick={() => setIsModalOpen(false)}
          >
            <div
              className="relative w-full max-w-2xl rounded-3xl bg-white p-6 text-left"
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                aria-label="Close"
                onClick={() => setIsModalOpen(false)}
                className="absolute right-5 top-4 text-3xl font-light text-slate-500 transition hover:text-slate-900"
              >
                ×
              </button>
              <div className="space-y-2 text-center">
                <p className="text-xs uppercase tracking-[0.4em] text-slate-500">{content.modalEyebrow}</p>
                <h2 className="text-3xl font-semibold text-slate-900 font-[var(--font-display)]">
                  {content.modalHeading}
                </h2>
                <p className="text-sm text-slate-600">{content.modalBody}</p>
              </div>

              <div className="mt-6 space-y-3">
                <p className="text-xs uppercase tracking-[0.3em] text-slate-500">{content.requestedPiecesLabel}</p>
                {items.length > 0 ? (
                  <div className="space-y-3">
                    {items.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-start justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3"
                      >
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{item.itemName || "Untitled"}</p>
                          <p className="text-xs text-slate-500">
                            {item.isCustomOrder
                              ? composeCustomOrderSpec(item.customOrder ?? {}) || "Custom request"
                              : [item.metal || "Mixed metal", item.stone || "Varied stone"].join(" • ")}
                          </p>
                          {item.size ? (
                            <p className="text-xs text-slate-500">Size {item.size}</p>
                          ) : null}
                          {item.requestedSize ? (
                            <p className="text-xs font-medium text-amber-700">
                              Requested: size {item.requestedSize}
                            </p>
                          ) : null}
                        </div>
                        <button
                          type="button"
                          onClick={() => removeItem(item.id)}
                          className="text-lg font-light text-slate-400 transition hover:text-slate-900"
                          aria-label="Remove item"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-600">{content.emptyBagModalMessage}</p>
                )}
              </div>

              <form className="mt-6 space-y-4" onSubmit={handleSendEmail}>
                <label className="block text-xs uppercase tracking-[0.3em] text-slate-500">
                  {content.emailLabel}
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder={content.emailPlaceholder}
                    className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
                  />
                </label>
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="text-xs uppercase tracking-[0.3em] text-slate-500">
                    {content.firstNameLabel}
                    <input
                      type="text"
                      value={firstName}
                      onChange={(event) => setFirstName(event.target.value)}
                      placeholder={content.firstNamePlaceholder}
                      className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
                    />
                  </label>
                  <label className="text-xs uppercase tracking-[0.3em] text-slate-500">
                    {content.lastNameLabel}
                    <input
                      type="text"
                      value={lastName}
                      onChange={(event) => setLastName(event.target.value)}
                      placeholder={content.lastNamePlaceholder}
                      className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
                    />
                  </label>
                </div>
                <label className="block text-xs uppercase tracking-[0.3em] text-slate-500">
                  {content.emailBodyLabel}
                  <textarea
                    value={body}
                    onChange={(event) => setBody(event.target.value)}
                    className="mt-2 min-h-[140px] w-full rounded-2xl border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
                  />
                </label>
                <button
                  type="submit"
                  disabled={!emailValid || items.length === 0 || isSubmitting}
                  className="w-full rounded-sm border border-royal/20 bg-royal px-6 py-3 text-sm font-semibold uppercase tracking-[0.4em] text-white transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isSubmitting ? content.sendingButtonText : content.sendButtonText}
                </button>
                {submitMessage ? (
                  <p className="text-center text-sm text-slate-600">{submitMessage}</p>
                ) : null}
              </form>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
